/**
 * ContactDetailModal — Read-Only-Snapshot aller Contact-Felder.
 *
 * Wird primär vom DuplicateActionModal geöffnet, wenn der User bei einer
 * Konflikt-Karte auf "Öffnen" klickt. Anders als das NotesModal (das dort
 * gewollt für Kontaktpflege existiert) ist dieser Dialog eine reine
 * Snapshot-Ansicht: was steht in Firestore, gruppiert nach Themen.
 *
 * Design:
 *   - Konsistent zum DuplicateActionModal (rounded card, X-Close, weiß).
 *   - Sections werden nur gerendert, wenn mindestens ein Feld gefüllt ist —
 *     leere Sections werden unterdrückt, damit das Modal nicht mit "—"
 *     zugeschüttet wird.
 *   - Custom Fields (cf_*) werden automatisch in eine eigene Section
 *     ausgelagert, damit sie nicht in Basisdaten reinschmieren.
 *   - Layout ist "Info-Grid": Label links (Uppercase-Mini), Value rechts.
 *   - Notes und Decision Makers bekommen kompakte Listen-Views.
 */

import React, { useMemo } from 'react';
import { modalOverlayAnim, modalCardAnim, sectionAnim } from './modalAnimations';

const KNOWN_KEYS = new Set(); // wird aus SECTIONS abgeleitet — für "Weitere Felder"

const SECTIONS = [
  {
    id: 'basic', title: 'Basic Info', icon: 'fa-id-card',
    fields: [
      { key: 'business_name',   label: 'Company' },
      { key: 'lead_email',      label: 'Email(s)', mono: true },
      { key: 'lead_website',    label: 'Website', link: true },
      { key: 'lead_telephone',  label: 'Phone' },
      { key: 'lead_type',       label: 'Type' },
    ],
  },
  {
    id: 'location', title: 'Location', icon: 'fa-location-dot',
    fields: [
      { key: 'lead_address',  label: 'Address' },
      { key: 'lead_postcode', label: 'Postcode' },
      { key: 'lead_city',     label: 'City' },
      { key: 'lead_country',  label: 'Country' },
    ],
  },
  {
    id: 'business', title: 'Business', icon: 'fa-briefcase',
    fields: [
      { key: 'lead_industry',    label: 'Industry' },
      { key: 'lead_description', label: 'Description', full: true },
      { key: 'lead_rating',      label: 'Rating' },
      { key: 'lead_reviews',     label: 'Reviews' },
      { key: 'lead_stars',       label: 'Stars' },
      { key: 'lead_instagram_followers', label: 'Instagram Followers' },
    ],
  },
  {
    id: 'pipeline', title: 'Pipeline & Outreach', icon: 'fa-satellite-dish',
    fields: [
      { key: 'lead_status',                label: 'Status' },
      { key: 'lead_contacted',             label: 'Contact Status' },
      { key: 'lead_contacted_timestamp',   label: 'Last contacted', dateRel: true },
      { key: 'lead_reply',                 label: 'Has replied?', bool: true },
      { key: 'has_prepared_email',         label: 'Prepared email pending?', bool: true },
      { key: 'lead_score',                 label: 'Lead Score' },
      { key: 'lead_pacing',                label: 'Pacing' },
      { key: 'lead_template',              label: 'Template' },
      { key: 'flowEndStatus',              label: 'Flow End' },
    ],
  },
  {
    id: 'meta', title: 'Metadata', icon: 'fa-clock',
    fields: [
      { key: 'timestamp',    label: 'Created', dateRel: true },
      { key: 'updated_at',   label: 'Last edited', dateRel: true },
      { key: 'import_name',  label: 'Import' },
      { key: 'isDisqualified', label: 'Disqualified?', bool: true },
      { key: 'merged_into',  label: 'Merged into', mono: true },
    ],
  },
];
// KNOWN_KEYS füllen für die "Weitere Felder"-Section
SECTIONS.forEach((s) => s.fields.forEach((f) => KNOWN_KEYS.add(f.key)));
// Zusätzlich System-Felder ausblenden, die im UI nichts bringen.
const SYSTEM_KEYS = new Set([
  'id', '_id',
  'business_name_normalized', 'lead_description_normalized', 'lead_email_normalized',
  'lead_email_dedup', 'business_name_dedup', 'lead_domain_dedup',
  'has_email', 'search_trigrams', 'search_terms',
  'lead_notes',              // eigene Section
  'lead_decision_makers',    // eigene Section
  'lead_socials',            // eigene Section
  'tags',                    // eigene Section
  'warmupFlow', 'warmupFlowHistory',
  'potential_score', 'potential_analysis',
  'leadSignals',
  'lead_last_edit',
  'lead_image',
  'flowEndedAt', 'disqualifiedAt', 'disqualifiedReason',
  'flow_sort_priority',
  'created_at',
]);

// Rausch-Filter: interne State-Flags, Queue-Marker, Backfill-Flags,
// AI-Processing-Metadaten etc. sind für den User bedeutungslos und wirken
// verwirrend. Ein einzelnes Blacklist-Set wäre schlecht wartbar — hier
// erwischen wir sie über Namens-Muster.
function isNoiseKey(key) {
  if (typeof key !== 'string') return true;
  if (key.startsWith('_')) return true;
  const patterns = [
    /^is_ready/i,
    /^ready_for_/i,
    /^needs_(spy|research|classify|reclassify|refresh|reprocess|scan|rescan|refetch)/i,
    /^has_prepared_?email_?ai/i,
    /^spy_/i,
    /_spy_/i,
    /^research_(status|state|attempt|error|version)/i,
    /^automation_/i,
    /^auto_/i,
    /_processed$/i,
    /_backfilled$/i,
    /_backfill_/i,
    /_migrated$/i,
    /_locked?$/i,
    /_queued?$/i,
    /^classification_/i,
    /_hash$/i,
    /_signature$/i,
    /^ai_(generated|processed|version|model|confidence|attempt|error|status)/i,
    /_attempt_count$/i,
    /^gcs_/i,
    /^dedup_/i,
    /_ready$/i,
    /_pending$/i,
    /^pending_/i,
  ];
  return patterns.some((p) => p.test(key));
}

export default function ContactDetailModal({ contact, onClose }) {
  const notes = useMemo(() => Array.isArray(contact?.lead_notes) ? contact.lead_notes : [], [contact]);
  const decisionMakers = useMemo(() => parseDecisionMakers(contact?.lead_decision_makers), [contact]);
  const socials = useMemo(() => parseSocialsMap(contact?.lead_socials), [contact]);
  const tags = useMemo(() => {
    if (!contact?.tags || typeof contact.tags !== 'object' || Array.isArray(contact.tags)) return [];
    return Object.values(contact.tags).filter(Boolean);
  }, [contact]);
  const customFields = useMemo(() => {
    if (!contact) return [];
    return Object.keys(contact)
      .filter((k) => k.startsWith('cf_') && hasVal(contact[k]))
      .map((k) => ({ key: k, label: prettyLabel(k.replace(/^cf_/, '')), value: contact[k] }));
  }, [contact]);
  const otherFields = useMemo(() => {
    if (!contact) return [];
    return Object.keys(contact)
      .filter((k) =>
        !KNOWN_KEYS.has(k) &&
        !SYSTEM_KEYS.has(k) &&
        !k.startsWith('cf_') &&
        !isNoiseKey(k) &&
        hasVal(contact[k]),
      )
      .map((k) => ({ key: k, label: prettyLabel(k), value: contact[k] }));
  }, [contact]);

  if (!contact) return null;

  // Lokaler pro-Render Delay-Counter für gestaggerte Section-Reveal-Animation.
  // Jede sichtbare Section bekommt +60 ms — konditionell geskipppte zählen
  // nicht mit, sodass die sichtbaren Sections lückenlos hintereinander
  // reinfaden.
  const d = { i: 0 };
  const next = () => (d.i++) * 55;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}><i className="fa fa-address-card" aria-hidden /></span>
            <div style={styles.headerText}>
              <div style={styles.title}>{contact.business_name || '(no name)'}</div>
              <div style={styles.subtitle}>Contact Details</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={styles.closeBtn} aria-label="Close">×</button>
        </div>

        <div style={styles.body}>
          {SECTIONS.map((section) => {
            const rows = section.fields
              .map((f) => ({ ...f, value: contact[f.key] }))
              .filter((f) => hasVal(f.value));
            if (rows.length === 0) return null;
            return (
              <Section key={section.id} title={section.title} icon={section.icon} delay={next()}>
                <InfoGrid rows={rows} />
              </Section>
            );
          })}

          {socials.length > 0 && (
            <Section title="Social Links" icon="fa-share-nodes" delay={next()}>
              <div style={styles.pillsRow}>
                {socials.map((s, i) => {
                  const meta = socialMeta(s.platform);
                  return (
                    <a
                      key={i}
                      href={ensureUrl(s.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...styles.socialPill,
                        color: meta.color,
                        borderColor: hexToRgba(meta.color, 0.28),
                        background: hexToRgba(meta.color, 0.08),
                      }}
                      title={s.url}
                    >
                      <i className={meta.icon} aria-hidden style={styles.socialIcon} />
                      {meta.label}
                    </a>
                  );
                })}
              </div>
            </Section>
          )}

          {decisionMakers.length > 0 && (
            <Section title={`Decision Makers (${decisionMakers.length})`} icon="fa-users" delay={next()}>
              <div style={styles.dmGrid}>
                {decisionMakers.map((dm, i) => (
                  <DmCard key={i} dm={dm} />
                ))}
              </div>
            </Section>
          )}

          {tags.length > 0 && (
            <Section title="Tags" icon="fa-tag" delay={next()}>
              <div style={styles.pillsRow}>
                {tags.map((t, i) => (
                  <span
                    key={i}
                    style={{
                      ...styles.tagPill,
                      background: t.color ? hexToRgba(t.color, 0.14) : '#f1f5f9',
                      color: t.color || '#334155',
                      borderColor: t.color ? hexToRgba(t.color, 0.35) : '#e2e8f0',
                    }}
                  >
                    {t.icon && <i className={`fa ${t.icon}`} style={{ fontSize: 9 }} aria-hidden />}
                    {t.name || t.id}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {customFields.length > 0 && (
            <Section title="Custom Fields" icon="fa-sliders" delay={next()}>
              <InfoGrid rows={customFields} />
            </Section>
          )}

          {notes.length > 0 && (
            <Section title={`Notes (${notes.length})`} icon="fa-sticky-note" delay={next()}>
              <div style={styles.notesList}>
                {notes.slice(0, 6).map((n, i) => (
                  <div key={i} style={styles.noteRow}>
                    <div style={styles.noteHead}>
                      <span style={styles.noteType}>{n.type || 'Note'}</span>
                      <span style={styles.noteDate}>{formatDateShort(n.ts || n.timestamp)}</span>
                    </div>
                    {n.text && <div style={styles.noteText}>{String(n.text)}</div>}
                  </div>
                ))}
                {notes.length > 6 && (
                  <div style={styles.notesMore}>… and {notes.length - 6} more notes</div>
                )}
              </div>
            </Section>
          )}

          {otherFields.length > 0 && (
            <Section title="Additional Fields" icon="fa-ellipsis" delay={next()}>
              <InfoGrid rows={otherFields} compact />
            </Section>
          )}
        </div>

        <div style={styles.footer}>
          <div style={styles.footerId}>ID: <code>{contact.id}</code></div>
          <button type="button" onClick={onClose} style={styles.footerBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children, delay }) {
  return (
    <div style={{ ...styles.section, animation: sectionAnim(delay || 0) }}>
      <div style={styles.sectionHead}>
        <i className={`fa ${icon}`} style={styles.sectionIcon} aria-hidden />
        <span style={styles.sectionTitle}>{title}</span>
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function InfoGrid({ rows, compact }) {
  return (
    <div style={compact ? styles.gridCompact : styles.grid}>
      {rows.map((r) => (
        <div
          key={r.key}
          style={{
            ...styles.gridRow,
            ...(r.full ? styles.gridRowFull : {}),
          }}
        >
          <div style={styles.gridLabel}>{r.label}</div>
          <div style={styles.gridValue}>
            <ValueRender field={r} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Font-Awesome-Brand-Slug pro Plattform. Falls unbekannt: fällt auf
// generisches Link-Icon zurück. Die Farbe passt zur jeweiligen Marke.
const SOCIAL_META = {
  linkedin:  { icon: 'fa-brands fa-linkedin-in', color: '#0a66c2', label: 'LinkedIn' },
  instagram: { icon: 'fa-brands fa-instagram',   color: '#e1306c', label: 'Instagram' },
  facebook:  { icon: 'fa-brands fa-facebook-f',  color: '#1877f2', label: 'Facebook' },
  twitter:   { icon: 'fa-brands fa-x-twitter',   color: '#000000', label: 'X / Twitter' },
  x:         { icon: 'fa-brands fa-x-twitter',   color: '#000000', label: 'X / Twitter' },
  tiktok:    { icon: 'fa-brands fa-tiktok',      color: '#000000', label: 'TikTok' },
  youtube:   { icon: 'fa-brands fa-youtube',     color: '#ff0000', label: 'YouTube' },
  pinterest: { icon: 'fa-brands fa-pinterest-p', color: '#e60023', label: 'Pinterest' },
  github:    { icon: 'fa-brands fa-github',      color: '#111827', label: 'GitHub' },
  xing:      { icon: 'fa-brands fa-xing',        color: '#006567', label: 'Xing' },
  whatsapp:  { icon: 'fa-brands fa-whatsapp',    color: '#25d366', label: 'WhatsApp' },
  telegram:  { icon: 'fa-brands fa-telegram',    color: '#26a5e4', label: 'Telegram' },
  threads:   { icon: 'fa-brands fa-threads',     color: '#000000', label: 'Threads' },
  website:   { icon: 'fa fa-globe',              color: '#334155', label: 'Website' },
  web:       { icon: 'fa fa-globe',              color: '#334155', label: 'Website' },
};
function socialMeta(platform) {
  const key = String(platform || '').toLowerCase();
  return SOCIAL_META[key] || { icon: 'fa fa-link', color: '#64748b', label: platform };
}
function ensureUrl(v) {
  if (!v || typeof v !== 'string') return '';
  const s = v.trim();
  if (!s) return '';
  if (/^(https?:|mailto:|tel:)/i.test(s)) return s;
  return `https://${s.replace(/^\/+/, '')}`;
}

function DmCard({ dm }) {
  const name = dm.name || dm.fullName || 'Unknown';
  const role = dm.role || dm.position || '';
  const source = dm.source
    ? (dm.source === 'linkedin' ? 'LI' : dm.source === 'grok' ? 'AI' : 'Web')
    : '';
  const socials = (dm.socials && typeof dm.socials === 'object' && !Array.isArray(dm.socials))
    ? Object.entries(dm.socials).filter(([, v]) => typeof v === 'string' && v.trim())
    : [];
  return (
    <div style={styles.dmCard}>
      <div style={styles.dmCardHead}>
        <div style={styles.dmAvatar}><i className="fa fa-user-tie" aria-hidden /></div>
        <div style={styles.dmHeadText}>
          <div style={styles.dmName}>
            {name}
            {source && <span style={styles.dmSourceBadge} title={`Source: ${dm.source}`}>{source}</span>}
            {dm.emailVerified && (
              <span style={styles.dmVerified} title="Email verified">
                <i className="fa fa-circle-check" aria-hidden />
              </span>
            )}
          </div>
          {role && <div style={styles.dmRole}>{role}</div>}
        </div>
      </div>

      <div style={styles.dmContactList}>
        {dm.email && (
          <a
            href={`mailto:${dm.email}`}
            style={{
              ...styles.dmContactLine,
              ...(dm.emailValidationFailed ? styles.dmContactBad : {}),
            }}
            title={dm.emailValidationFailed ? (dm.emailNote || 'Invalid email') : dm.email}
          >
            <i className="fa fa-envelope" style={styles.dmContactIcon} aria-hidden />
            <span style={styles.dmContactText}>{dm.email}</span>
            {dm.emailValidationFailed && (
              <i className="fa fa-triangle-exclamation" style={{ fontSize: 10, color: '#dc2626' }} aria-hidden />
            )}
          </a>
        )}
        {(dm.phone || dm.telephone) && (
          <a href={`tel:${dm.phone || dm.telephone}`} style={styles.dmContactLine}>
            <i className="fa fa-phone" style={styles.dmContactIcon} aria-hidden />
            <span style={styles.dmContactText}>{dm.phone || dm.telephone}</span>
          </a>
        )}
      </div>

      {socials.length > 0 && (
        <div style={styles.dmSocials}>
          {socials.map(([platform, url]) => {
            const meta = socialMeta(platform);
            return (
              <a
                key={platform}
                href={ensureUrl(url)}
                target="_blank"
                rel="noopener noreferrer"
                title={meta.label}
                style={{ ...styles.dmSocialBtn, background: meta.color }}
              >
                <i className={meta.icon} aria-hidden />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ValueRender({ field }) {
  const v = field.value;
  if (v == null || v === '') return <span style={styles.gridEmpty}>—</span>;
  if (field.bool) return v === true ? <span style={styles.chipYes}>Yes</span> : <span style={styles.chipNo}>No</span>;
  if (field.dateRel) return <>{formatDateShort(v)} <span style={styles.gridSub}>({formatRelative(v)})</span></>;
  if (field.link && typeof v === 'string') {
    const href = v.startsWith('http') ? v : `https://${v}`;
    return <a href={href} target="_blank" rel="noopener noreferrer" style={styles.linkVal}>{v}</a>;
  }
  if (field.mono) return <span style={styles.mono}>{String(v)}</span>;
  if (typeof v === 'object') return <span style={styles.mono}>{JSON.stringify(v).slice(0, 200)}</span>;
  return <span>{String(v)}</span>;
}

/* ─── Helpers ──────────────────────────────────────────────────────── */

// snake_case / camelCase → "Snake Case" / "Camel Case" für Auto-Labels.
function prettyLabel(key) {
  if (typeof key !== 'string') return '';
  return key
    .replace(/^lead_/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function hasVal(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '' && v.trim() !== '-' && v.trim() !== 'null';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}
function parseDecisionMakers(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
}
function parseSocialsMap(raw) {
  if (!raw) return [];
  let obj = raw;
  if (typeof raw === 'string') {
    try { obj = JSON.parse(raw); } catch { return []; }
  }
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .filter(([, v]) => typeof v === 'string' && v.trim())
    .map(([platform, url]) => ({ platform, url }));
}
function formatDateShort(v) {
  if (!v) return '';
  const t = typeof v === 'string' ? Date.parse(v) : Number(v);
  if (!t || Number.isNaN(t)) return String(v);
  return new Date(t).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function formatRelative(v) {
  if (!v) return '';
  const t = typeof v === 'string' ? Date.parse(v) : Number(v);
  if (!t || Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const day = 86400000;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < day) return `${Math.round(diff / 3600000)} h ago`;
  if (diff < 30 * day) return `${Math.round(diff / day)} days ago`;
  if (diff < 365 * day) return `${Math.round(diff / (30 * day))} months ago`;
  return `${Math.round(diff / (365 * day))} years ago`;
}
function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return `rgba(148,163,184,${alpha})`;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const num = parseInt(full, 16);
  const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ─── Styles ───────────────────────────────────────────────────────── */

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 10005, padding: '5vh 16px', overflowY: 'auto',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
    animation: modalOverlayAnim,
  },
  modal: {
    background: 'white', borderRadius: 16, width: '100%', maxWidth: 1100,
    boxShadow: '0 24px 64px rgba(15,23,42,0.4)',
    display: 'flex', flexDirection: 'column',
    maxHeight: '90vh',
    animation: modalCardAnim,
    transformOrigin: 'center top',
  },
  header: {
    padding: '16px 22px 14px 22px', borderBottom: '1px solid #f1f5f9',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 },
  headerIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
    color: '#6897B7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, flexShrink: 0,
  },
  headerText: { minWidth: 0 },
  title: {
    fontSize: 17, fontWeight: 700, color: '#0f172a', lineHeight: 1.2,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  subtitle: { fontSize: 11.5, color: '#64748b', marginTop: 2 },
  closeBtn: {
    background: 'transparent', border: 'none', fontSize: 26, lineHeight: 1,
    color: '#94a3b8', cursor: 'pointer', padding: '0 4px', flexShrink: 0,
  },
  body: {
    padding: '10px 22px 4px 22px', overflowY: 'auto',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  section: {
    borderTop: '1px solid #f1f5f9', paddingTop: 10, marginTop: 2,
  },
  sectionHead: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
  },
  sectionIcon: { fontSize: 11, color: '#64748b' },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
    color: '#475569',
  },
  sectionBody: {},
  // Responsive: bei breitem Modal 3 Spalten (mehr Info pro Screen-Höhe),
  // auf schmalen Screens fällt es automatisch auf 1-2 Spalten zurück.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '4px 24px',
  },
  gridCompact: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '2px 16px',
  },
  gridRow: {
    display: 'flex', flexDirection: 'column', gap: 2, padding: '3px 0', minWidth: 0,
  },
  gridRowFull: {
    gridColumn: '1 / -1',
  },
  gridLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
    color: '#94a3b8',
  },
  gridValue: {
    fontSize: 13, color: '#0f172a', wordBreak: 'break-word', lineHeight: 1.45,
    minWidth: 0,
  },
  gridEmpty: { color: '#cbd5e1' },
  gridSub: { color: '#94a3b8', fontSize: 11, fontWeight: 400 },
  linkVal: { color: '#2563eb', textDecoration: 'none' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 },
  chipYes: {
    display: 'inline-block', padding: '1px 8px', borderRadius: 999,
    background: '#ecfdf5', color: '#065f46', fontSize: 11, fontWeight: 600,
    border: '1px solid #a7f3d0',
  },
  chipNo: {
    display: 'inline-block', padding: '1px 8px', borderRadius: 999,
    background: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 600,
    border: '1px solid #e2e8f0',
  },
  pillsRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  socialPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '5px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
    background: '#f8fafc', color: '#334155', border: '1px solid #e2e8f0',
    textDecoration: 'none',
  },
  socialIcon: { fontSize: 12, opacity: 0.8 },
  tagPill: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
    border: '1px solid',
  },
  dmGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8,
  },
  dmCard: {
    padding: 10, background: '#f8fafc', borderRadius: 10,
    border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 8,
  },
  dmCardHead: { display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 },
  dmAvatar: {
    width: 34, height: 34, borderRadius: 10, flexShrink: 0,
    background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
    color: '#6897B7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14,
  },
  dmHeadText: { minWidth: 0, flex: 1 },
  dmName: {
    fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.25,
    display: 'flex', alignItems: 'center', gap: 6,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  dmSourceBadge: {
    display: 'inline-block', padding: '1px 6px', borderRadius: 4,
    background: '#e0e7ff', color: '#4338ca', fontSize: 9, fontWeight: 700,
    letterSpacing: 0.4,
  },
  dmVerified: { color: '#10b981', fontSize: 11 },
  dmRole: {
    fontSize: 11, color: '#64748b', marginTop: 2,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  dmContactList: { display: 'flex', flexDirection: 'column', gap: 3 },
  dmContactLine: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    color: '#2563eb', fontSize: 11.5, textDecoration: 'none',
    fontFamily: 'ui-monospace, monospace',
    minWidth: 0, wordBreak: 'break-all',
  },
  dmContactBad: { color: '#dc2626' },
  dmContactIcon: { fontSize: 10, opacity: 0.7, flexShrink: 0 },
  dmContactText: { overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 },
  dmSocials: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  dmSocialBtn: {
    width: 26, height: 26, borderRadius: 6,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', textDecoration: 'none', fontSize: 12,
    transition: 'transform 120ms ease',
  },
  notesList: { display: 'flex', flexDirection: 'column', gap: 6 },
  noteRow: {
    padding: '8px 10px', background: '#f8fafc', borderRadius: 8,
    border: '1px solid #e2e8f0',
  },
  noteHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    marginBottom: 3,
  },
  noteType: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    color: '#6366f1',
  },
  noteDate: { fontSize: 10, color: '#94a3b8' },
  noteText: { fontSize: 12, color: '#334155', lineHeight: 1.5 },
  notesMore: {
    fontSize: 11, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center',
    padding: '4px 0',
  },
  footer: {
    padding: '10px 22px 14px 22px', borderTop: '1px solid #f1f5f9',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    flexShrink: 0,
  },
  footerId: {
    fontSize: 10, color: '#94a3b8', fontFamily: 'ui-monospace, monospace',
  },
  footerBtn: {
    background: '#0f172a', color: 'white', border: 'none',
    borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 600,
    cursor: 'pointer',
  },
};
